import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import {
  MapPin,
  Phone,
  Mail,
  Globe,
  Clock,
  ArrowLeft,
  Star,
  BadgeCheck,
  Crown,
  Pencil,
  UserCheck,
  QrCode,
  StarOff,
  Home,
  TrendingUp,
  Key,
  Users,
  Building2,
  Handshake,
  Target,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { API } from "@/App";
import { resolveAssetUrl } from "@/lib/assetUrl";
import FavoriteButton from "@/components/FavoriteButton";
import ShareButton from "@/components/ShareButton";
import ReviewsSection from "@/components/ReviewsSection";
import BusinessExternalReviewsSection from "@/components/BusinessExternalReviewsSection";
import { trackEvent } from "@/lib/analytics";

// ── Design tokens ────────────────────────────────────────────────────────
// Every visual choice here maps back to the Julius Scott reference mockup
// (see the task brief). Nothing is Julius-specific — the layout renders
// each field from `business` so any realtor listed under the "Real Estate"
// category gets the same premium presentation automatically.

// Default realtor tagline shown when no per-business tagline is stored.
const DEFAULT_TAGLINE = "Your Trusted Partner in Buying, Selling & Investing";

// Default specialty triplet displayed under the hero when a realtor has
// not filled in their own services yet.
const DEFAULT_SPECIALTIES = [
  "Residential Sales",
  "Investment Properties",
  "Client-Focused Results",
];

// Fallback dusk-property backdrop used when a realtor's image_url is not
// set. Rendered behind the gradient overlay so the hero card is never
// visually empty. The overlay hides most of it so the specific image
// choice is largely aesthetic.
const FALLBACK_HERO_BG =
  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1600&q=80";

// Small icon map for specialty chips beneath the hero. Falls through to
// a keyring icon so unknown specialties still render.
function iconForSpecialty(label) {
  const key = String(label || "").toLowerCase();
  if (key.includes("residential") || key.includes("home") || key.includes("family")) return Home;
  if (key.includes("invest") || key.includes("commercial")) return TrendingUp;
  if (key.includes("client") || key.includes("service") || key.includes("focused"))
    return Key;
  if (key.includes("first") || key.includes("buyer")) return Users;
  if (key.includes("luxury") || key.includes("estate")) return Building2;
  if (key.includes("negotiat") || key.includes("partner")) return Handshake;
  return Target;
}

// ── BC Score display ─────────────────────────────────────────────────────
function BCScoreDisplay({ score, count }) {
  const full = Math.floor(score);
  const half = score % 1 >= 0.5;
  return (
    <div className="flex items-center gap-2">
      <div className="flex">
        {Array.from({ length: 5 }, (_, i) => (
          <Star
            key={`s-${i}`}
            className={`w-5 h-5 ${
              i < full
                ? "fill-tbc-gold text-tbc-gold"
                : i === full && half
                ? "fill-tbc-gold/50 text-tbc-gold"
                : "text-gray-300"
            }`}
          />
        ))}
      </div>
      <span className="text-lg font-heading text-tbc-navy">
        {score > 0 ? score : "—"}/5
      </span>
      <span className="text-sm text-gray-500 font-body">
        ({count} {count === 1 ? "review" : "reviews"})
      </span>
    </div>
  );
}

function BCScoreInput({ value, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-1">
      {Array.from({ length: 5 }, (_, i) => (
        <button
          key={`i-${i}`}
          type="button"
          onClick={() => onChange(i + 1)}
          onMouseEnter={() => setHover(i + 1)}
          onMouseLeave={() => setHover(0)}
          className="p-0.5"
        >
          <Star
            className={`w-6 h-6 transition-colors ${
              (hover || value) > i
                ? "fill-tbc-gold text-tbc-gold"
                : "text-gray-300"
            }`}
          />
        </button>
      ))}
      <span className="ml-2 text-lg font-heading text-tbc-navy">
        {value || hover || 0}/5
      </span>
    </div>
  );
}

// ── Hero card ────────────────────────────────────────────────────────────
// The showpiece of the whole layout. Renders a dark navy card, portrait
// on the right, badges + text on the left, and gold specialty icons at
// the bottom. Composition scales down cleanly on mobile via clamp() —
// no fixed pixel positioning, no text overlapping the portrait.
function RealtorHeroCard({ business }) {
  const heroPortrait = resolveAssetUrl(business.image_url);
  const tagline = business.tagline || DEFAULT_TAGLINE;
  const rawSpecs =
    (business.specialties && business.specialties.length > 0
      ? business.specialties
      : business.services) || DEFAULT_SPECIALTIES;
  const specialties = rawSpecs.slice(0, 3);

  // Strip common realtor role suffixes so the h1 stays clean when the
  // business name is stored like "Julius Scott - Real Estate Expert".
  // The role line already carries "Real Estate Expert" underneath.
  const displayName = business.name.replace(
    /\s*[-–—]\s*(real\s*estate\s*(expert|agent|professional|advisor)?|realtor|broker)\s*$/i,
    "",
  );

  const marketRegion = (business.state || business.city || "").trim();

  return (
    <div
      className="relative overflow-hidden rounded-3xl bg-tbc-navy shadow-xl ring-1 ring-tbc-navy/10"
      data-testid="realtor-hero-card"
    >
      {/* Portrait sits on the RIGHT half of the card. The container's
          own width scales with the viewport, and the image is object-cover
          so Julius' face stays anchored via object-position: right top. */}
      <div className="absolute inset-y-0 right-0 w-[60%] sm:w-[55%] lg:w-[52%] xl:w-[48%]" aria-hidden="true">
        {heroPortrait ? (
          <img
            src={heroPortrait}
            alt=""
            className="h-full w-full object-cover"
            style={{ objectPosition: "50% 42%" }}
          />
        ) : (
          <img
            src={FALLBACK_HERO_BG}
            alt=""
            className="h-full w-full object-cover"
          />
        )}
        {/* Left-edge fade blends the portrait into the navy text column,
            hiding any hard seam and any bleed from cropped source assets. */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-tbc-navy via-tbc-navy/60 to-transparent sm:w-32" />
      </div>

      {/* Text column: left ~40-45%. Grid so text never overlaps portrait. */}
      <div className="relative grid min-h-[460px] grid-cols-[1fr_auto] gap-3 p-5 sm:min-h-[520px] sm:p-8 lg:min-h-[600px] lg:p-10 xl:min-h-[640px]">
        <div className="flex max-w-[62%] flex-col justify-between sm:max-w-[55%] lg:max-w-[50%]">
          {/* Top: badges */}
          <div className="flex flex-wrap items-center gap-2">
            <span
              data-testid="realtor-category-badge"
              className="inline-flex items-center rounded-full bg-tbc-gold px-3 py-1 text-xs font-semibold text-tbc-navy sm:text-sm"
            >
              Real Estate
            </span>
            {business.is_verified && (
              <span
                data-testid="realtor-verified-badge"
                className="inline-flex items-center gap-1 rounded-full bg-tbc-gold px-3 py-1 text-xs font-semibold text-tbc-navy sm:text-sm"
              >
                <BadgeCheck className="h-3.5 w-3.5" />
                Verified
              </span>
            )}
            {business.is_founding_member && business.founding_member_number ? (
              <Link
                to={`/founding-100#${String(business.founding_member_number).padStart(3, "0")}`}
                data-testid="realtor-founding-badge"
                className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-tbc-gold to-amber-400 px-3 py-1 text-xs font-bold text-tbc-navy sm:text-sm hover:shadow-lg transition-all"
                title={`Founding Business #${business.founding_member_number} — View the Founding 100`}
              >
                <Crown className="h-3.5 w-3.5" />
                Founding Business #{String(business.founding_member_number).padStart(3, "0")}
              </Link>
            ) : business.is_founding_member ? (
              <span
                data-testid="realtor-founding-badge"
                className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-tbc-gold to-amber-400 px-3 py-1 text-xs font-bold text-tbc-navy sm:text-sm"
              >
                <Crown className="h-3.5 w-3.5" />
                Founding Business
              </span>
            ) : null}
          </div>

          {/* Middle: name + region + role + tagline */}
          <div className="my-4 sm:my-6">
            <h1
              data-testid="realtor-name"
              className="font-heading text-white leading-[1.02] tracking-tight"
              style={{ fontSize: "clamp(1.6rem, 6vw, 3.25rem)" }}
            >
              {displayName}
            </h1>
            {marketRegion && (
              <p
                className="mt-3 font-heading text-white/95 leading-tight"
                style={{ fontSize: "clamp(1.05rem, 3.4vw, 1.85rem)" }}
                data-testid="realtor-market-line"
              >
                {marketRegion}
              </p>
            )}
            <p
              className="font-heading text-white/95 leading-tight"
              style={{ fontSize: "clamp(1.05rem, 3.4vw, 1.85rem)" }}
              data-testid="realtor-role-line"
            >
              Real Estate Expert
            </p>
            <p
              className="mt-3 font-heading italic text-tbc-gold leading-snug"
              style={{ fontSize: "clamp(0.85rem, 2.4vw, 1.25rem)" }}
              data-testid="realtor-tagline"
            >
              {tagline}
            </p>
            <div className="mt-3 h-0.5 w-20 bg-tbc-gold/80 sm:w-24" />
          </div>

          {/* Bottom: specialties row (max 3) */}
          <div
            className="grid grid-cols-3 gap-2 pt-2 sm:gap-4"
            data-testid="realtor-specialties"
          >
            {specialties.map((label, idx) => {
              const Icon = iconForSpecialty(label);
              return (
                <div
                  key={`spec-${idx}`}
                  className="relative flex flex-col items-start"
                >
                  {idx > 0 && (
                    <div className="absolute -left-1 top-1 hidden h-8 w-px bg-tbc-gold/30 sm:block" />
                  )}
                  <Icon
                    className="mb-1 h-6 w-6 text-tbc-gold sm:h-7 sm:w-7"
                    strokeWidth={1.6}
                  />
                  <span className="text-[11px] font-medium leading-tight text-white sm:text-sm">
                    {label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right spacer: pushes text left of the portrait region. */}
        <div className="w-[38%] sm:w-[45%] lg:w-[48%] xl:w-[45%]" aria-hidden="true" />
      </div>
    </div>
  );
}

// ── Admin action buttons row (below the hero card, never overlapping) ───
function RealtorActionButtons({
  business,
  isAdmin,
  onEdit,
  onAssignOwner,
  onClaimQR,
  onToggleGoogle,
  onDelete,
}) {
  if (!isAdmin) return null;
  const disabled = !!business.google_reviews_disabled;
  return (
    <div
      className="grid grid-cols-4 gap-2 sm:gap-3"
      data-testid="realtor-action-buttons"
    >
      <button
        type="button"
        onClick={onEdit}
        data-testid="realtor-edit-btn"
        className="flex flex-col items-center justify-center gap-1.5 rounded-2xl border border-gray-200 bg-white px-2 py-3 text-tbc-navy shadow-sm transition hover:border-tbc-navy/30 hover:shadow-md sm:py-4"
      >
        <Pencil className="h-5 w-5" strokeWidth={1.8} />
        <span
          className="font-body font-medium leading-tight"
          style={{ fontSize: "clamp(0.7rem, 2.2vw, 0.95rem)" }}
        >
          Edit
        </span>
      </button>

      <button
        type="button"
        onClick={onAssignOwner}
        data-testid="realtor-assign-owner-btn"
        className="flex flex-col items-center justify-center gap-1.5 rounded-2xl bg-tbc-gold px-2 py-3 text-tbc-navy shadow-sm transition hover:bg-tbc-gold/90 hover:shadow-md sm:py-4"
      >
        <UserCheck className="h-5 w-5" strokeWidth={1.8} />
        <span
          className="text-center font-body font-semibold leading-tight"
          style={{ fontSize: "clamp(0.7rem, 2.2vw, 0.95rem)" }}
        >
          Assign<span className="hidden sm:inline"> </span>
          <br className="sm:hidden" />
          owner
        </span>
      </button>

      {!business.is_claimed ? (
        <button
          type="button"
          onClick={onClaimQR}
          data-testid="realtor-claim-qr-btn"
          className="flex flex-col items-center justify-center gap-1.5 rounded-2xl border border-gray-200 bg-white px-2 py-3 text-tbc-navy shadow-sm transition hover:border-tbc-navy/30 hover:shadow-md sm:py-4"
        >
          <QrCode className="h-5 w-5" strokeWidth={1.8} />
          <span
            className="text-center font-body font-medium leading-tight"
            style={{ fontSize: "clamp(0.7rem, 2.2vw, 0.95rem)" }}
          >
            Claim<br className="sm:hidden" /> QR
          </span>
        </button>
      ) : (
        <button
          type="button"
          onClick={onDelete}
          data-testid="realtor-delete-btn"
          className="flex flex-col items-center justify-center gap-1.5 rounded-2xl border border-red-200 bg-white px-2 py-3 text-red-600 shadow-sm transition hover:border-red-300 hover:shadow-md sm:py-4"
          title="Delete business"
        >
          <Trash2 className="h-5 w-5" strokeWidth={1.8} />
          <span
            className="text-center font-body font-medium leading-tight"
            style={{ fontSize: "clamp(0.7rem, 2.2vw, 0.95rem)" }}
          >
            Delete
          </span>
        </button>
      )}

      <button
        type="button"
        onClick={onToggleGoogle}
        data-testid="realtor-toggle-google-btn"
        className={`flex flex-col items-center justify-center gap-1.5 rounded-2xl border bg-white px-2 py-3 text-tbc-navy shadow-sm transition hover:shadow-md sm:py-4 ${
          disabled ? "border-tbc-navy/25" : "border-red-200"
        }`}
      >
        <StarOff className="h-5 w-5" strokeWidth={1.8} />
        <span
          className="text-center font-body font-medium leading-tight"
          style={{ fontSize: "clamp(0.7rem, 2.2vw, 0.95rem)" }}
        >
          {disabled ? (
            <>
              Enable<br className="sm:hidden" /> Google<br className="sm:hidden" />{" "}
              reviews
            </>
          ) : (
            <>
              Disable<br className="sm:hidden" /> Google<br className="sm:hidden" />{" "}
              reviews
            </>
          )}
        </span>
      </button>
    </div>
  );
}

// ── BC Score card ────────────────────────────────────────────────────────
function RealtorBCScoreCard({ business, user, onOpenReview }) {
  const isOwner = user && user.user_id === business.owner_id;
  return (
    <div
      className="rounded-2xl bg-tbc-cream/70 p-5 sm:p-6"
      data-testid="realtor-bc-score-card"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-heading text-lg text-tbc-navy sm:text-xl">
            BC Score
          </h3>
          <div className="mt-2">
            <BCScoreDisplay
              score={business.bc_score || 0}
              count={business.bc_score_count || 0}
            />
          </div>
          {isOwner && (
            <p
              className="mt-3 text-sm italic text-gray-600"
              data-testid="realtor-own-business-note"
            >
              You can&apos;t review your own business.
            </p>
          )}
        </div>
        {!isOwner && (
          <Button
            onClick={onOpenReview}
            className="rounded-full bg-tbc-gold px-5 py-2.5 font-body font-semibold text-tbc-navy hover:bg-tbc-gold/90"
            data-testid="realtor-rate-btn"
          >
            <Star className="mr-2 h-4 w-4" />
            Rate This Business
          </Button>
        )}
      </div>
    </div>
  );
}

// ── Contact / hours / website info card ──────────────────────────────────
function RealtorContactBlock({ business }) {
  const hasContact =
    business.address ||
    business.city ||
    business.phone ||
    business.email ||
    business.website;
  const hasHours = business.hours && Object.keys(business.hours).length > 0;
  if (!hasContact && !hasHours) return null;

  return (
    <div className="space-y-2 font-body text-[15px] text-gray-700">
      {(business.address || business.city) && (
        <div className="flex items-start gap-3">
          <MapPin className="mt-0.5 h-5 w-5 flex-none text-tbc-gold" />
          <div>
            {business.address && <p>{business.address}</p>}
            {(business.city || business.state) && (
              <p>
                {[business.city, business.state].filter(Boolean).join(", ")}
                {business.zip_code ? ` ${business.zip_code}` : ""}
                {!business.address && business.state ? " & Surrounding Areas" : ""}
              </p>
            )}
          </div>
        </div>
      )}
      {business.phone && (
        <div className="flex items-center gap-3">
          <Phone className="h-5 w-5 flex-none text-tbc-gold" />
          <a
            href={`tel:${business.phone}`}
            className="hover:text-tbc-navy"
            onClick={() =>
              trackEvent(business.business_id, "contact_click", {
                metadata: { channel: "phone" },
              })
            }
          >
            {business.phone}
          </a>
        </div>
      )}
      {business.email && (
        <div className="flex items-center gap-3">
          <Mail className="h-5 w-5 flex-none text-tbc-gold" />
          <a
            href={`mailto:${business.email}`}
            className="break-all hover:text-tbc-navy"
            onClick={() =>
              trackEvent(business.business_id, "contact_click", {
                metadata: { channel: "email" },
              })
            }
          >
            {business.email}
          </a>
        </div>
      )}
      {business.website && (
        <div className="flex items-center gap-3">
          <Globe className="h-5 w-5 flex-none text-tbc-gold" />
          <a
            href={business.website}
            target="_blank"
            rel="noopener noreferrer"
            className="break-all hover:text-tbc-navy"
            onClick={() =>
              trackEvent(business.business_id, "contact_click", {
                metadata: { channel: "website" },
              })
            }
          >
            {business.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}
          </a>
        </div>
      )}
      {hasHours && (
        <div className="flex items-start gap-3 pt-1">
          <Clock className="mt-0.5 h-5 w-5 flex-none text-tbc-gold" />
          <div className="space-y-0.5">
            {Object.entries(business.hours).map(([day, hours]) => (
              <p key={day}>
                <span className="capitalize text-gray-500">{day}: </span>
                <span className="text-tbc-navy">{hours}</span>
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main layout ──────────────────────────────────────────────────────────
export default function RealtorProfileLayout({
  business,
  user,
  isAdmin,
  isFavorited,
  setIsFavorited,
  onEdit,
  onAssignOwner,
  onClaimQR,
  onDelete,
  onToggleGoogleReviews,
  reviewsRefreshKey,
  onReviewSubmitted,
}) {
  const [reviewOpen, setReviewOpen] = useState(false);
  const [newReview, setNewReview] = useState({ score: 0, comment: "" });
  const [submitting, setSubmitting] = useState(false);

  const submitReview = async () => {
    if (newReview.score < 1) {
      toast.error("Please select a BC Score");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/businesses/${business.business_id}/reviews`,
        newReview,
        { withCredentials: true },
      );
      toast.success("Review submitted!");
      setReviewOpen(false);
      setNewReview({ score: 0, comment: "" });
      onReviewSubmitted?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-[1440px] px-4 pb-16 pt-6 sm:px-6 lg:px-10">
      {/* Back + favorite row */}
      <div className="flex items-center justify-between gap-3">
        <Link
          to="/directory"
          className="inline-flex items-center gap-2 font-body text-gray-700 hover:text-tbc-navy"
          data-testid="realtor-back-to-directory"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Directory
        </Link>
        <div className="flex items-center gap-2">
          <FavoriteButton
            businessId={business.business_id}
            initial={isFavorited}
            size="md"
            onChange={setIsFavorited}
          />
          <ShareButton
            title={business.name}
            text={`Check out ${business.name} on The Blk Community`}
            variant="icon"
            testId="realtor-share-btn"
            onShare={() => trackEvent(business.business_id, "share_click")}
          />
        </div>
      </div>

      {/* Two-column layout at ≥lg, single column below */}
      <div className="mt-4 grid grid-cols-1 gap-6 lg:mt-6 lg:grid-cols-12 lg:gap-8">
        {/* LEFT: Hero + admin action buttons */}
        <div className="space-y-4 lg:col-span-8 lg:space-y-5">
          <RealtorHeroCard business={business} />
          <RealtorActionButtons
            business={business}
            isAdmin={isAdmin}
            onEdit={onEdit}
            onAssignOwner={onAssignOwner}
            onClaimQR={onClaimQR}
            onDelete={onDelete}
            onToggleGoogle={onToggleGoogleReviews}
          />
        </div>

        {/* RIGHT: BC Score + About + Contact stack (below hero on mobile) */}
        <div className="space-y-6 lg:col-span-4">
          <RealtorBCScoreCard
            business={business}
            user={user}
            onOpenReview={() => setReviewOpen(true)}
          />

          {business.description && (
            <section data-testid="realtor-about-section">
              <h2 className="font-heading text-2xl text-tbc-navy sm:text-3xl">
                About
              </h2>
              <p
                className="mt-3 whitespace-pre-wrap font-body leading-relaxed text-gray-700"
                data-testid="realtor-description"
              >
                {business.description}
              </p>
            </section>
          )}

          <section data-testid="realtor-contact-section">
            <RealtorContactBlock business={business} />
          </section>
        </div>
      </div>

      {/* Reviews + external Google reviews sit full-width below both columns */}
      <div className="mt-10 space-y-8">
        <ReviewsSection
          business={business}
          currentUser={user}
          refreshKey={reviewsRefreshKey}
          onWriteReview={
            user && user.user_id === business.owner_id
              ? undefined
              : () => setReviewOpen(true)
          }
        />
        <BusinessExternalReviewsSection businessId={business.business_id} />
      </div>

      {/* Review dialog */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              Rate {business.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 pt-4">
            <div>
              <label className="mb-3 block text-sm font-medium text-gray-700">
                Your BC Score
              </label>
              <BCScoreInput
                value={newReview.score}
                onChange={(score) => setNewReview({ ...newReview, score })}
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Your Review (Optional)
              </label>
              <Textarea
                value={newReview.comment}
                onChange={(e) =>
                  setNewReview({ ...newReview, comment: e.target.value })
                }
                placeholder="Share your experience with this realtor..."
                rows={4}
              />
            </div>
            <Button
              onClick={submitReview}
              disabled={submitting || newReview.score < 1}
              className="w-full rounded-full bg-tbc-navy py-6 text-white hover:bg-tbc-navy/90"
              data-testid="realtor-submit-review-btn"
            >
              {submitting ? "Submitting..." : "Submit Review"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
